'use client';

import { useIdleMount } from '@repo/web-chrome/lib/use-idle-mount';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import type { IClipSwiperItem } from './clip-carousel';

// Idle-deferred + ssr:false + in-view gated: keeps the player JS off the initial bundle
// AND keeps the autoplay clip from downloading megabytes while the phone is below the
// fold (on mobile the article now renders first, so the player only mounts when the
// visitor actually scrolls to it; on lg+ the phone is in the first viewport and mounts
// at idle as before). The rootMargin is deliberately 0: short articles put the phone
// just below the fold, and any preload margin re-admits the video into the load-time
// bandwidth fight the gate exists to prevent. The server-rendered poster <img> behind
// this overlay fills the slot until the active clip's video paints over it.
const ClipPhoneSwiper = dynamic(
  () => import('./clip-phone-swiper').then((mod) => mod.ClipPhoneSwiper),
  { ssr: false },
);

const PRELOAD_ROOT_MARGIN = '0px';

interface IClipPhoneSwiperLazyProps {
  clips: IClipSwiperItem[];
  language: string;
}

export function ClipPhoneSwiperLazy({
  clips,
  language,
}: IClipPhoneSwiperLazyProps): React.JSX.Element {
  const ready = useIdleMount();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || inView) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
        }
      },
      { rootMargin: PRELOAD_ROOT_MARGIN },
    );
    observer.observe(host);
    return (): void => {
      observer.disconnect();
    };
  }, [inView]);

  return (
    <div ref={hostRef} className="absolute inset-0">
      {ready && inView ? <ClipPhoneSwiper clips={clips} language={language} /> : null}
    </div>
  );
}
